import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ApiHttp, SCHEMA_VERSION } from '@core/api/api-http';

import { ContactService } from './contact.service';
import { ContactSummary, Dependent } from '../models';
import { Individual } from '../models/individual';
import { Relationship } from '../models/individual/relationship';
import { Residence } from '../models/individual/residence';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class IndividualService {
  constructor(
    private http: ApiHttp,
    private readonly contactService: ContactService
  ) {}

  getIndividual(individualId: string): Observable<Individual.SchemaContract> {
    return this.http.getEntity<Individual.SchemaContract>(
      environment.clientsApiUrl,
      `individuals/${individualId}`,
      SCHEMA_VERSION.INDIVIDUAL,
      ApiHttp.getStandardOptions()
    );
  }

  deleteIndividual(individualId: string): Observable<object> {
    return this.http.deleteEntity(
      environment.clientsApiUrl,
      `individuals/${individualId}`,
      SCHEMA_VERSION.INDIVIDUAL
    );
  }

  getSummary(individualId: string): Observable<Individual.Summary> {
    return this.http
      .getEntity<Individual.SchemaContract>(
        environment.clientsApiUrl,
        `individuals/${individualId}`,
        SCHEMA_VERSION.INDIVIDUAL,
        ApiHttp.getStandardOptions()
      )
      .pipe(
        map((x) => {
          return {
            id: individualId,
            name: x.name.first + ' ' + x.name.last
          };
        })
      );
  }

  getIndividualDefinition(): Observable<Individual.SchemaPropertyDefinition> {
    return this.http.getEntity(
      environment.clientsApiUrl,
      'individuals/schema',
      SCHEMA_VERSION.INDIVIDUAL,
      ApiHttp.getStandardOptions()
    );
  }

  getResidenceDefinition(): Observable<Residence.SchemaPropertyDefinition> {
    return this.http.getEntity(
      environment.clientsApiUrl,
      'individuals/schema/residences',
      SCHEMA_VERSION.INDIVIDUAL,
      ApiHttp.getStandardOptions()
    );
  }

  getRelationshipDefinition(): Observable<Relationship.SchemaPropertyDefinition> {
    return this.http.getEntity(
      environment.clientsApiUrl,
      'individuals/schema/relationships',
      SCHEMA_VERSION.INDIVIDUAL,
      ApiHttp.getStandardOptions()
    );
  }

  getDependentDefinition(): Observable<Dependent.SchemaPropertyDefinition> {
    return this.http.getEntity(
      environment.clientsApiUrl,
      'individuals/schema/dependents',
      SCHEMA_VERSION.INDIVIDUAL,
      ApiHttp.getStandardOptions()
    );
  }

  getIndividualForDisplay(id: string): Observable<Individual.Model> {
    return this.http
      .getEntity<Individual.SchemaContract>(
        environment.clientsApiUrl,
        `individuals/${id}`,
        SCHEMA_VERSION.INDIVIDUAL,
        ApiHttp.getStandardOptions()
      )
      .pipe(map((payload) => Individual.toModel(id, payload)));
  }

  getIndividualWithSpecificContacts(id: string): Observable<{
    individual: Individual.Model;
    contacts: ContactSummary[];
  }> {
    return this.getIndividualForDisplay(id).pipe(
      switchMap((individual) => {
        const contactIds: string[] = [
          ...this.getDependents(individual),
          ...this.getGuardians(individual)
        ];

        return this.contactService.getSpecificContacts(contactIds).pipe(
          map((contacts) => ({
            individual,
            contacts
          }))
        );
      })
    );
  }

  private getGuardians(individual: Individual.Model): string[] {
    return (individual.dependents || []).reduce((r, d) => {
      if (d.otherGuardian) {
        r.push(d.otherGuardian);
      }
      return r;
    }, [] as string[]);
  }

  private getDependents(individual: Individual.Model): string[] {
    return (individual.relationships || []).map((r) => r.id);
  }

  updateIndividual(
    individualId: string,
    change: Partial<Individual.Model>
  ): Observable<any> {
    return this.http.put(
      environment.clientsApiUrl,
      `individuals/${individualId}`,
      Individual.toSchemaContract(change),
      SCHEMA_VERSION.INDIVIDUAL,
      ApiHttp.getStandardOptions()
    );
  }

  deleteRelationship(id: string) {
    return this.http.deleteEntity(
      environment.clientsApiUrl,
      `relationships/${id}`,
      SCHEMA_VERSION.INDIVIDUAL
    );
  }  
}
